import type { ConnectorSurface, ExclusionReason } from '@ralph/contracts'
import { PROVIDER_LABEL } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'
import type { FoldersFmt } from '@/i18n/messages/en/knowledgeFolders.ts'

/**
 * `knowledgeFolders` em português do Brasil.
 *
 * Declarado `: Messages['knowledgeFolders']`, nunca inferido: uma chave que falta, uma chave a
 * mais ou uma assinatura que mudou é erro de `tsc`, não uma lacuna silenciosa em tempo de
 * execução (PRD-27 LOC-011).
 *
 * ── O vocabulário é o ratificado (ADR-0018) ──
 *
 * *pasta conectada*, *base de conhecimento*, *documento*, *trecho*, *conta*, *pessoa*, *grupo*,
 * *agente*, *administrador*. Registro `você`, informal-profissional. Nunca *ficheiro*, *ecrã*,
 * *utilizador* nem *partilha* — e nunca *usuário* onde se fala de gente.
 *
 * ── Três armadilhas que esta tradução evita de propósito ──
 *
 *  - **Concordância.** Uma pasta é feminina, um arquivo e um documento são masculinos, uma
 *    verificação é feminina. "Paused" vira *Pausada* e "Not started" vira *Não iniciada*,
 *    porque quem está pausada é a pasta.
 *  - **Zero é singular.** `Intl.PluralRules('pt-BR').select(0)` devolve `one`, então "0
 *    documento" — cada entrada que conta pergunta ao CLDR em vez de comparar com 1.
 *  - **A preposição já vem pronta.** `Intl.RelativeTimeFormat` em português devolve "há 5 min"
 *    com a preposição dentro, então nenhuma frase daqui acrescenta outra: é
 *    "verificada ${quando}", nunca "verificada em ${quando}" (LOC-022).
 */

/** As formas de plural do CLDR. `other` existe em toda língua; as outras são opcionais. */
type PluralForms = Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }

/** Repetido aqui, e não importado do inglês, para que cada idioma fique inteiro em um arquivo. */
const plural = (fmt: FoldersFmt, count: number, forms: PluralForms): string =>
  forms[pluralCategory(fmt.locale, count)] ?? forms.other

/** "1 arquivo" / "2 arquivos" — e "0 arquivo", que é o que o português faz no zero. */
const fileCount = (fmt: FoldersFmt, count: number): string =>
  `${fmt.formatCount(count)} ${plural(fmt, count, { one: 'arquivo', other: 'arquivos' })}`

/** O que uma verificação fez, como marcação de contagem e não como frase. Vazio quando nada mudou. */
const tally = (fmt: FoldersFmt, added: number, changed: number, removed: number, withdrawn: number): string => {
  const parts: string[] = []
  if (added > 0)
    parts.push(`${fmt.formatCount(added)} ${plural(fmt, added, { one: 'adicionado', other: 'adicionados' })}`)
  if (changed > 0)
    parts.push(`${fmt.formatCount(changed)} ${plural(fmt, changed, { one: 'alterado', other: 'alterados' })}`)
  if (removed + withdrawn > 0) {
    const gone = removed + withdrawn
    parts.push(`${fmt.formatCount(gone)} ${plural(fmt, gone, { one: 'retirado', other: 'retirados' })}`)
  }
  return parts.join(', ')
}

/** Exaustivo sobre o enum, como no inglês: um décimo motivo tem de quebrar a compilação aqui também. */
const EXCLUSION_LABEL = {
  unsupported: 'um formato que não conseguimos ler',
  too_large: 'grande demais',
  too_old: 'fora pelo filtro de data',
  type: 'fora pelo filtro de tipo',
  rule: 'fora',
  not_selected: 'não escolhido',
  removed_by_steward: 'removido à mão',
  folder: 'fora',
  base_full: 'a base de conhecimento está cheia'
} satisfies Record<ExclusionReason, string>

const SURFACE_HINT = {
  sharepoint: 'Uma biblioteca de documentos de um site.',
  onedrive: 'Os arquivos da própria pessoa.',
  drive: 'Uma pasta no Meu Drive ou em um drive compartilhado.',
  s3: 'Um prefixo em um bucket.'
} satisfies Record<ConnectorSurface, string>

const UNSUPPORTED_BODY = 'Eles são contados e pulados; nada neles muda qualquer outra coisa.'

export const knowledgeFolders: Messages['knowledgeFolders'] = {
  tab: 'Pastas conectadas',
  baseNotice: (fmt, count) =>
    pluralCategory(fmt.locale, count) === 'one'
      ? 'Uma pasta conectada precisa de alguém antes de sincronizar.'
      : `${fmt.formatCount(count)} pastas conectadas precisam de alguém antes de sincronizar.`,

  empty: 'Nada está sendo acompanhado ainda',
  emptyBody: (baseName) =>
    `Uma pasta conectada mantém esta base de conhecimento em dia sozinha. Os arquivos da pasta que você escolher são lidos para dentro de ${baseName} e, quando alguém adiciona, altera ou remove um arquivo lá, isso vale aqui também. Nada é escrito de volta, nunca.`,
  connect: 'Conectar uma pasta',
  loading: 'Carregando as pastas conectadas…',
  unavailable: 'Não foi possível carregar as pastas conectadas',
  unavailableBody: 'Isto é a plataforma, não o provedor. Nada foi desconectado.',

  // --- contas ----------------------------------------------------------------
  accounts: {
    title: 'Contas',
    summary: (fmt, count) =>
      count === 0
        ? 'Nenhuma conta ainda'
        : `${fmt.formatCount(count)} ${plural(fmt, count, { one: 'conta', other: 'contas' })}`,
    delegatedHint: (surface) =>
      `Esta plataforma vai poder ler o que esta conta consegue ler no ${surface}, e só ler. Ela nunca escreve, move, renomeia nem exclui nada lá.`,
    delegatedStops: (name) => `Esta pasta sincroniza como ${name}; ela para quando a conta dessa pessoa é desativada.`,
    applicationHint:
      'Uma conta da empresa, configurada por um administrador. Ela não pertence a ninguém em particular, então continua funcionando quando alguém sai.',
    s3AdminOnly: 'Um bucket S3 é lido com chaves — uma conta da empresa —, então quem conecta é um administrador.',
    companyAdminOnly:
      'Contas da empresa — uma chave S3, uma conta de serviço do Google, o app Microsoft da organização — são adicionadas e usadas por administradores.',
    readsThroughOwner: (owner) =>
      `Esta pasta lê pela entrada de ${owner}, então só essa pessoa ou um administrador pode verificá-la, mudar o que ela carrega ou retomá-la. Qualquer pessoa que mantém a base pode pausá-la, desconectá-la ou passá-la para a própria entrada.`,
    readsThroughCompany:
      'Esta pasta lê por uma conta da empresa, então só um administrador pode verificá-la, mudar o que ela carrega ou retomá-la. Qualquer pessoa que mantém a base pode pausá-la, desconectá-la ou passá-la para a própria entrada.',
    companyAccount: 'conta da empresa',
    show: 'mostrar',
    hide: 'ocultar',
    keep: 'Manter',
    // Sem preposição: `formatAgo` já devolve "há 3 dias" com a dela, e além de uma semana
    // devolve uma data — "Entrou há 3 dias" e "Entrou 20 de set." funcionam, "Entrou em há 3
    // dias" não funcionaria (LOC-022).
    signedIn: (when) => `Entrou ${when}`,
    usedBy: (fmt, count) =>
      count === 0
        ? 'Não é usada por nenhuma pasta'
        : `Usada por ${fmt.formatCount(count)} ${plural(fmt, count, { one: 'pasta', other: 'pastas' })}`,
    signInWith: (provider) => `Entrar com ${PROVIDER_LABEL[provider]}`,
    signInAgain: 'Entrar de novo',
    useKeys: 'Usar estas chaves',
    remove: 'Remover',
    removeConfirm: (fmt, label, folders) =>
      folders === 0
        ? `Remover ${label}? Nada está usando esta conta.`
        : `Remover ${label}? ${fmt.formatCount(folders)} ${plural(fmt, folders, { one: 'pasta que a usa vai pausar', other: 'pastas que a usam vão pausar' })}. Nada é retirado.`,
    notConfigured: (fmt, missing) =>
      missing.length === 0
        ? ''
        : `${plural(fmt, missing.length, { one: 'Só um provedor está configurado', other: 'Só alguns provedores estão configurados' })} nesta instalação — um administrador adiciona ${fmt.formatList(missing)}.`,
    whereFiles: 'Onde os arquivos estão',
    surfaceHint: SURFACE_HINT,
    chooseAccount: 'De quem são os olhos',
    optionLabel: (account, provider, application) =>
      `${account} — ${provider}${application ? ' (conta de aplicativo)' : ''}`,
    differentAccount: (provider) => `Entrar com outra conta ${PROVIDER_LABEL[provider]}`,
    opensSignIn: (provider) => `Abre a página de entrada do próprio ${PROVIDER_LABEL[provider]} nesta janela.`,
    signInFailed: 'Essa entrada não foi concluída.',
    theProvider: 'O provedor',
    signInFailedBody: (provider) =>
      `Nada foi conectado e nada mudou aqui. Se você quis mesmo permitir, entre de novo — ${provider} pede que você confirme o que esta plataforma pode ler antes de entregar qualquer coisa.`,
    change: 'Trocar',
    useServiceAccount: 'Usar uma conta de serviço',
    useApplicationAccount: 'Usar uma conta de aplicativo',
    checking: 'Verificando…',
    connectAccount: 'Conectar',
    tenantLabel: 'Id do tenant',
    tenantHint:
      'O id de diretório (tenant) do registro de aplicativo que um administrador consentiu para toda a organização.',
    serviceAccountLabel: 'Chave da conta de serviço',
    serviceAccountHint:
      'Cole o arquivo de chave JSON do Google Cloud. Compartilhe com o e-mail da conta de serviço as pastas que ela deve ler; esse é todo o acesso que ela recebe.',
    s3: {
      keyId: 'Id da chave de acesso',
      secret: 'Chave de acesso secreta',
      sessionToken: 'Session token (só para chaves temporárias)',
      region: 'Região',
      bucket: 'Bucket',
      endpoint: 'Endpoint (só para armazenamento compatível com S3)',
      pathStyle: 'Endereçamento path-style (MinIO e a maioria dos gateways)',
      policyTitle: 'As únicas permissões de que a chave precisa',
      policyHint:
        'Anexe isto ao IAM user ou à role da chave antes de conectar. Ela lê os nomes dos objetos do bucket (a primeira verificação precisa disso) e o conteúdo apenas deste prefixo. Nada nela pode escrever.',
      copy: 'Copiar política',
      copied: 'Copiado'
    }
  },

  // --- encontrar a pasta -----------------------------------------------------
  browse: {
    title: 'Onde acompanhar',
    findSite: 'Procurar um site…',
    search: 'Buscar',
    siteHint: 'Sites que você consegue abrir no SharePoint. Nada além do que você já pode abrir é listado.',
    driveHint: 'O que esta conta consegue abrir no Google Drive. Nada além do que ela já pode abrir é listado.',
    onedriveUser: 'OneDrive de quem',
    onedriveUserHint:
      'O nome de entrada da pessoa, como ana@empresa.com.br. Uma conta de aplicativo lê qualquer OneDrive da organização.',
    onedriveUserPlaceholder: 'ana@empresa.com.br',
    openUser: 'Abrir',
    whereYouAre: 'Onde você está',
    open: (name) => `Abrir ${name}`,
    choose: (name) => `Escolher ${name}`,
    chooseAction: 'Escolher',
    chooseThis: 'Acompanhar esta pasta',
    counting: 'Contando…',
    columns: {
      name: 'Nome',
      contents: 'Conteúdo',
      choose: 'Escolher'
    },
    items: (fmt, count) => `${fmt.formatCount(count)} ${plural(fmt, count, { one: 'item', other: 'itens' })}`,
    filesHere: (fmt, count) => (count === 0 ? 'nenhum arquivo nesta pasta' : `e ${fileCount(fmt, count)} nesta pasta`),
    nothingHere: 'Nada aqui dentro que dê para abrir.',
    more: 'Mostrar mais',
    loading: 'Perguntando ao provedor…'
  },

  // --- a prévia --------------------------------------------------------------
  preview: {
    title: 'O que vai ser carregado',
    counting: (fmt, files) =>
      files === 0 ? 'Contando o que tem aqui…' : `Ainda contando — ${fmt.formatCount(files)} arquivos até agora.`,
    summary: (fmt, readable, total, size, folders) =>
      readable === total
        ? `${plural(fmt, total, { one: `${fmt.formatCount(total)} arquivo pode ser lido`, other: `Todos os ${fmt.formatCount(total)} arquivos podem ser lidos` })} — ${size} em ${fmt.formatCount(folders)} ${plural(fmt, folders, { one: 'pasta', other: 'pastas' })}.`
        : `${fmt.formatCount(readable)} de ${fmt.formatCount(total)} ${plural(fmt, total, { one: 'arquivo', other: 'arquivos' })} ${plural(fmt, readable, { one: 'pode ser lido', other: 'podem ser lidos' })} — ${size} em ${fmt.formatCount(folders)} ${plural(fmt, folders, { one: 'pasta', other: 'pastas' })}.`,
    nothingIncluded:
      'Nada nesta pasta está incluído, então nada daqui pode ser citado. Marque uma pasta acima ou limpe os filtros.',
    nothingReadable: (fmt, total, baseName) =>
      `Nenhum dos ${fmt.formatCount(total)} arquivos daqui pode ser lido. Conectar esta pasta não acrescentaria nada a ${baseName}.`,
    empty: 'Esta pasta não tem nenhum arquivo.',
    // Cada chip carrega o número junto porque é o número que rege a concordância do que vem
    // depois: "1 será carregado", "3 serão carregados".
    legend: {
      included: (fmt, count) =>
        `${fmt.formatCount(count)} ${plural(fmt, count, { one: 'será carregado', other: 'serão carregados' })}`,
      unsupported: (fmt, count) => `${fmt.formatCount(count)} em formato que não lemos`,
      tooLarge: (fmt, count) =>
        `${fmt.formatCount(count)} ${plural(fmt, count, { one: 'grande demais', other: 'grandes demais' })}`,
      filtered: (fmt, count) => `${fmt.formatCount(count)} fora pelos filtros`,
      leftOut: (fmt, count) => `${fmt.formatCount(count)} fora por você`
    },
    unsupportedHeading: (fmt, count) =>
      `${fmt.formatCount(count)} ${plural(fmt, count, { one: 'arquivo está', other: 'arquivos estão' })} em um formato que não conseguimos ler`,
    unsupportedBody: UNSUPPORTED_BODY,
    // O `label` é prosa do servidor, em inglês, e não tem código nenhum atrás dele — então aqui
    // ele é contado, não flexionado. "2 × a Word file" é honesto; inventar morfologia
    // portuguesa em cima de uma etiqueta inglesa não seria.
    unsupportedDetail: (kinds) => `${kinds.map((k) => `${k.count} × ${k.label}`).join(', ')}. ${UNSUPPORTED_BODY}`,
    tooLargeHeading: (fmt, count) =>
      `${fmt.formatCount(count)} ${plural(fmt, count, { one: 'arquivo é grande demais', other: 'arquivos são grandes demais' })}`,
    tooLargeBody: (limit, largest) =>
      `Um documento pode ter até ${limit}.${largest ? ` O maior aqui tem ${largest}.` : ''}`,
    unknownSize: (fmt, count) =>
      `${fmt.formatCount(count)} ${plural(fmt, count, {
        one: 'é um arquivo do Google Docs, Sheets ou Slides',
        other: 'são arquivos do Google Docs, Sheets ou Slides'
      })}: ${plural(fmt, count, { one: 'exportado', other: 'exportados' })} na hora de carregar, então o tamanho ${plural(fmt, count, { one: 'dele', other: 'deles' })} só é conhecido aí.`,
    listThem: 'Listar',
    hideThem: 'Ocultar',
    backToFolders: 'Voltar para as pastas',
    andMore: (fmt, count) => `e mais ${fmt.formatCount(count)}`,
    truncated: (fmt, cap) =>
      `Esta pasta tem mais de ${fmt.formatCount(cap)} arquivos. Isso é mais do que uma pasta conectada deveria carregar. Os números aqui cobrem os primeiros ${fmt.formatCount(cap)}. Escolha uma subpasta, ou limite por tipo ou por data, para que chegue o que você quis.`,
    otherKinds: (fmt, count, kinds) =>
      `${fmt.formatCount(count)} em ${fmt.formatCount(kinds)} ${plural(fmt, kinds, { one: 'outro tipo', other: 'outros tipos' })}`,
    suggestion: (fmt, count, year) => `${fmt.formatCount(count)} arquivos daqui não mudam desde ${year}.`,
    leaveThemOut: 'Deixar de fora',
    willLoad: (fmt, files, size, leftOut) =>
      `${fmt.formatCount(files)} ${plural(fmt, files, { one: 'arquivo vai ser carregado', other: 'arquivos vão ser carregados' })} agora, ${size} no total.${leftOut > 0 ? ` ${fmt.formatCount(leftOut)} ${plural(fmt, leftOut, { one: 'ficou de fora e está listado acima', other: 'ficaram de fora e estão listados acima' })}.` : ''} De agora em diante, tudo o que for adicionado a esta pasta também é carregado, e tudo o que for removido lá é retirado daqui.`,
    willLoadCounting: (fmt, files) =>
      `Ainda contando — ${fmt.formatCount(files)} arquivos até agora. Começar agora carrega tudo o que está nesta pasta, incluindo o que a contagem ainda não alcançou. Você pode deixar qualquer parte de fora depois.`,
    start: 'Começar a sincronizar',
    startDisabledEmpty: 'Não dá para ler nada aqui, então não há o que começar.',
    startDisabledNothingIncluded: 'Nada está incluído ainda, então não há o que começar.',
    cancel: 'Cancelar'
  },

  // --- seleção ---------------------------------------------------------------
  selection: {
    title: 'O que está incluído',
    pathLabel: 'Caminho da pasta',
    columns: {
      included: 'Incluído',
      name: 'Nome',
      files: 'Arquivos',
      size: 'Tamanho'
    },
    include: (name, detail) => `Incluir ${name} — ${detail}`,
    includedMark: 'incluído',
    leftOutMark: 'fora',
    keptUpToDate: 'sempre em dia — novos arquivos aqui também são carregados',
    leftOutFolder: 'fora — novos arquivos aqui também ficam de fora',
    someOf: (fmt, included, total) =>
      `${fmt.formatCount(included)} de ${fmt.formatCount(total)} ${plural(fmt, total, { one: 'arquivo', other: 'arquivos' })}`,
    allFilteredOut: (fmt, total) =>
      `0 de ${fmt.formatCount(total)} ${plural(fmt, total, { one: 'arquivo', other: 'arquivos' })} — nenhum pode ser carregado`,
    files: (fmt, count) => fileCount(fmt, count),
    filesInFolder: (fmt, count, included) =>
      count === 0
        ? 'nenhum arquivo nesta pasta'
        : included === count
          ? `e ${fileCount(fmt, count)} nesta pasta — ${plural(fmt, count, { one: 'incluído', other: 'todos incluídos' })}`
          : `e ${fileCount(fmt, count)} nesta pasta — ${fmt.formatCount(included)} ${plural(fmt, included, { one: 'incluído', other: 'incluídos' })}`,
    tooManyToTick:
      'Escolha por pasta, tipo ou data aqui. Depois que os arquivos forem carregados, você pode deixar qualquer um deles de fora, um por um.',
    showMore: (fmt, shown, total) =>
      `Mostrar mais ${fmt.formatCount(Math.min(100, total - shown))} de ${fmt.formatCount(total)}`,
    skipList: 'Pular a lista de pastas',
    filters: {
      allTypes: 'todos os tipos',
      onlyTypes: (types) => `só ${types.map((t) => t.replace(/^\./, '')).join(', ')}`,
      noExtension: '(nenhuma)',
      anySize: 'qualquer tamanho',
      upTo: (size) => `até ${size}`,
      anyTime: 'alterados a qualquer momento',
      since: (date) => `alterados desde ${date}`,
      typesLegend: 'Tipos de arquivo',
      sizeLegend: 'Maior arquivo para carregar',
      sizeLabel: 'Megabytes',
      sizeUnit: 'MB',
      sizeHint: (limit) => `Um documento pode ter até ${limit}, seja qual for o valor aqui.`,
      dateLegend: 'Alterados desde',
      lastYear: 'último ano',
      lastThreeYears: 'últimos 3 anos',
      anyTimeChoice: 'qualquer momento',
      apply: 'Aplicar',
      clear: 'Limpar'
    },
    pending: {
      region: 'Mudanças pendentes',
      working: 'Calculando o que muda…',
      both: (fmt, added, withdrawn) =>
        `${fmt.formatCount(added)} ${plural(fmt, added, { one: 'arquivo vai ser adicionado', other: 'arquivos vão ser adicionados' })}. ${fmt.formatCount(withdrawn)} ${plural(fmt, withdrawn, { one: 'vai ser retirado', other: 'vão ser retirados' })} de todas as respostas futuras.`,
      addedOnly: (fmt, added) =>
        `${fmt.formatCount(added)} ${plural(fmt, added, { one: 'arquivo vai ser adicionado', other: 'arquivos vão ser adicionados' })}.`,
      nothing: 'Nada muda: o que está incluído continua como está.',
      body: 'Colocar algo de volta depois carrega o arquivo de novo do provedor. As citações dadas nesse meio-tempo dizem que o documento foi retirado.',
      apply: 'Aplicar estas mudanças',
      applying: 'Aplicando…',
      applyLabel: (fmt, added, withdrawn) =>
        `Aplicar estas mudanças — ${fmt.formatCount(added)} adicionados, ${fmt.formatCount(withdrawn)} retirados`,
      discard: 'Descartar',
      applied: (fmt, added, withdrawn) =>
        added === 0 && withdrawn === 0
          ? 'Aplicado. Nada precisou mudar.'
          : `Aplicado. ${fmt.formatCount(added)} para adicionar, ${fmt.formatCount(withdrawn)} ${plural(fmt, withdrawn, { one: 'retirado', other: 'retirados' })}.`,
      unsaved: (fmt, added, withdrawn) =>
        `Você tem ${fmt.formatCount(added)} arquivos para adicionar e ${fmt.formatCount(withdrawn)} para retirar que ainda não foram aplicados.`,
      keepEditing: 'Continuar editando',
      discardAndClose: 'Descartar e fechar',
      applyAndClose: 'Aplicar e fechar'
    }
  },

  // --- o limite de acesso (PRD-25 D-8, PRD-26 D-38) --------------------------
  access: {
    you: 'você',
    // "gestores de X" excluiria metade das pessoas pela desinência; "quem gerencia X" é neutro
    // e é a mesma construção da frase ratificada "Só quem mantém" (ADR-0018).
    groupManagers: (group) => `quem gerencia ${group}`,
    headline: (readers, baseName) => {
      switch (readers.kind) {
        case 'private':
          return `Só ${readers.who} vai poder buscar o que você incluir aqui.`
        case 'groups':
          return `Todas as pessoas que podem usar ${baseName} vão poder buscar tudo o que você incluir aqui.`
        case 'everyone':
          return 'Todas as pessoas que entram vão poder buscar tudo o que você incluir aqui.'
      }
    },
    groupsAre: (fmt, names, people) =>
      `Isso é todo mundo em ${fmt.formatList(names)}${people !== null ? ` — ${fmt.formatCount(people)} ${plural(fmt, people, { one: 'pessoa', other: 'pessoas' })}` : ''}.`,
    provider: (surface, baseName) =>
      `Não importa quem consegue abrir estes arquivos no ${surface}: depois que um arquivo é carregado, qualquer pessoa que pode usar ${baseName} consegue encontrá-lo em uma resposta.`,
    privateLater: (baseName) =>
      `Se ${baseName} for compartilhada com mais pessoas depois, esta pasta pede que você confirme de novo antes de carregar qualquer coisa a mais.`,
    restrictedWayOut:
      'Se alguns destes arquivos devem chegar a menos pessoas, conecte-os a uma base de conhecimento compartilhada de forma mais restrita.',
    everyoneBody: (baseName, surface) =>
      `${baseName} está compartilhada com todo mundo. Os arquivos desta pasta que só algumas pessoas conseguem abrir no ${surface} vão poder ser lidos por todas elas depois de carregados.`,
    everyoneAck: 'Entendo que todas as pessoas que entram vão poder buscar estes arquivos.',
    everyoneWayOut: (baseName) =>
      `Se não é isso que você quer, compartilhe ${baseName} de forma mais restrita antes, ou conecte esta pasta a uma base de conhecimento que já seja.`,
    agents: (fmt, count, baseName) =>
      pluralCategory(fmt.locale, count) === 'one'
        ? `Um agente também responde a partir de ${baseName} para todas as pessoas que ele alcança, incluindo quem não pode usar ${baseName}.`
        : `${fmt.formatCount(count)} agentes também respondem a partir de ${baseName} para todas as pessoas que eles alcançam, incluindo quem não pode usar ${baseName}.`,
    widened: (baseName) =>
      `Quem alcança ${baseName} mudou depois que esta pasta foi conectada — ela foi compartilhada com mais pessoas, foi movida, ou um agente passou a poder responder a partir dela para todas as pessoas que ele alcança. Confirme quem pode buscar o que esta pasta carrega para continuar sincronizando.`,
    acknowledge: 'Confirmar e continuar sincronizando',
    readBy: (fmt, readers) => {
      switch (readers.kind) {
        case 'private':
          return `só ${readers.who}`
        case 'groups':
          return `todo mundo em ${fmt.formatList(readers.names)}${readers.people !== null ? ` (${fmt.formatCount(readers.people)} ${plural(fmt, readers.people, { one: 'pessoa', other: 'pessoas' })})` : ''}`
        case 'everyone':
          return 'todas as pessoas que entram'
      }
    }
  },

  // --- a linha da pasta e o estado dela --------------------------------------
  standing: {
    // Quem está acompanhando, pausada ou não iniciada é a PASTA, que é feminina: os
    // particípios concordam com ela e não com "folder" (D-21).
    watching: 'Acompanhando',
    dueNow: 'a qualquer momento',
    countAgain: 'Contar de novo',
    signInWithMine: 'Entrar com minha conta',
    loading: (fmt, done, total) => `Carregando ${fmt.formatCount(done)} de ${fmt.formatCount(total)}…`,
    counting: 'Contando o que tem aqui…',
    countingSoFar: (fmt, files) => `${fmt.formatCount(files)} arquivos até agora`,
    notStarted: 'Não iniciada',
    notStartedHint: 'Escolha o que carregar',
    notStartedRow: 'Contada e esperando. Abra para escolher o que carregar.',
    paused: 'Pausada',
    pausedHint: 'Nada novo está chegando. O que está aqui fica e continua respondendo.',
    needsSignIn: 'Precisa entrar de novo',
    ownerMustSignIn: (owner) => `${owner} precisa entrar de novo; só essa pessoa pode renovar esta conta.`,
    // "desde ${quando}" daria "desde há 2 dias". O travessão evita a preposição e funciona
    // tanto com "há 2 dias" quanto com "20 de set." (LOC-022).
    needsSignInHint: (since) =>
      since
        ? `Nada novo chegou desde a última vez — ${since}. O que foi carregado fica e continua respondendo.`
        : 'Nada novo está chegando. O que foi carregado fica e continua respondendo.',
    // Sem o artigo: a mesma entrada é o rótulo do selo na linha e o título do bloco de
    // problema, e o selo é um espaço estreito onde o português já corre 15–25% mais longo.
    failed: 'Última verificação falhou',
    slowed: (surface) => `${surface} está pedindo para diminuirmos o ritmo`,
    slowedHint: (when) =>
      `${when ? `Próxima tentativa ${when}.` : 'Vai ser tentada de novo em breve.'} Nada é perdido.`,
    ownerLeft: (name) => `${name} entrou com esta conta, e a conta dessa pessoa foi encerrada.`,
    ownerLeftBody: () =>
      'As pastas que usam essa conta pararam; nada foi retirado. Qualquer pessoa que mantém a base pode passá-las para a própria entrada, ou um administrador para outra conta.',
    checked: (when) => `verificada ${when}`,
    next: (when) => `próxima ${when}`,
    included: (fmt, included, total) =>
      `${fmt.formatCount(included)} de ${fmt.formatCount(total)} ${plural(fmt, total, { one: 'arquivo incluído', other: 'arquivos incluídos' })}`,
    rootGone: (surface, name) => `${name} não está mais no ${surface}.`,
    rootGoneBody: (fmt, docs) =>
      `A sincronização parou e nada foi retirado — ${plural(fmt, docs, { one: `o ${fmt.formatCount(docs)} documento que já está aqui continua respondendo`, other: `os ${fmt.formatCount(docs)} documentos que já estão aqui continuam respondendo` })}. Se a pasta foi movida, conecte a nova e depois desconecte esta. Se ela foi excluída de propósito, desconecte esta pasta e escolha o que acontece com os documentos.`,
    forbidden: (name) => `Esta conta não consegue mais abrir ${name}.`,
    forbiddenBody: (fmt, docs, account) =>
      `A sincronização parou e nada foi retirado — ${plural(fmt, docs, { one: `o ${fmt.formatCount(docs)} documento que já está aqui continua respondendo`, other: `os ${fmt.formatCount(docs)} documentos que já estão aqui continuam respondendo` })}. Peça a quem gerencia aquela pasta para dar acesso de leitura a ${account} e verifique de novo. Se o acesso foi removido de propósito, desconecte esta pasta e escolha o que acontece com os documentos.`,
    emptyListing: (name) => `${name} voltou vazia.`,
    emptyListingBody:
      'Onde havia arquivos, a última verificação não encontrou nenhum. Nada foi retirado. Verifique de novo — ou, se a pasta foi esvaziada de propósito, desconecte-a e escolha o que acontece com os documentos.',
    interrupted: 'A última verificação foi interrompida antes de terminar. Nada foi retirado; ela vai rodar de novo.',
    checkAgain: 'Verificar de novo',
    open: 'Abrir'
  },

  // --- o detalhe da pasta ----------------------------------------------------
  detail: {
    drawerLabel: (path) => `${path} — pasta conectada`,
    where: 'O que ela acompanha',
    account: 'conta',
    readBy: 'lida por',
    lastChecked: 'última verificação',
    nextCheck: 'próxima verificação',
    loaded: 'carregados',
    loadedOf: (fmt, done, total) => `${fmt.formatCount(done)} de ${fmt.formatCount(total)}`,
    problems: 'Não carregados',
    leftOutByYou: 'Deixados de fora por você',
    leftOutByYouBody:
      'Arquivos que você removeu à mão e pastas que você desmarcou. Eles não são carregados, e os arquivos novos dentro de uma pasta desmarcada também não.',
    leftOutFolderKind: (fmt, files) => `pasta, ${fileCount(fmt, files)}`,
    putBack: 'Colocar de volta',
    nothingLeftOut: 'Nada foi deixado de fora à mão.',
    orphanRules: (fmt, count) =>
      `${fmt.formatCount(count)} ${plural(fmt, count, {
        one: 'escolha anterior não se aplica mais — os arquivos que ela nomeava não estão mais na pasta.',
        other: 'escolhas anteriores não se aplicam mais — os arquivos que elas nomeavam não estão mais na pasta.'
      })}`,
    history: 'Histórico',
    noHistory: 'Nenhuma verificação ainda.',
    nothingChanged: 'nada tinha mudado',
    checkingRow: 'verificando…',
    stoppedSignIn: 'parou — era preciso entrar de novo',
    stopped: (reason) => `parou — ${reason}`,
    stoppedUnknown: 'não terminou',
    triggeredBy: (who) => `por ${who}`,
    counted: (fmt, files) => `${fileCount(fmt, files)} ${plural(fmt, files, { one: 'contado', other: 'contados' })}`,
    syncTally: (fmt, added, changed, removed, withdrawn) => tally(fmt, added, changed, removed, withdrawn),
    checkNow: 'Verificar agora',
    checking: 'Verificando…',
    checkedResult: (fmt, added, changed, removed, withdrawn) => {
      const parts = tally(fmt, added, changed, removed, withdrawn)
      return parts === '' ? 'Verificado agora mesmo. Nada tinha mudado.' : `Verificado agora mesmo. ${parts}.`
    },
    checkQueued: 'Verificando em segundo plano — a linha se atualiza quando terminar.',
    pause: 'Pausar',
    pauseConfirm: (fmt, name, docs) =>
      `Pausar ${name}? Nada novo chega e nada é retirado. ${plural(fmt, docs, { one: `O ${fmt.formatCount(docs)} documento que já está aqui continua respondendo`, other: `Os ${fmt.formatCount(docs)} documentos que já estão aqui continuam respondendo` })}. Retomar recupera todas as mudanças feitas enquanto ela esteve pausada.`,
    resume: 'Retomar',
    howOften: 'Com que frequência verificar',
    interval: {
      soon: 'Assim que der — mais ou menos a cada 30 minutos',
      soonHint: (surface) => `As mudanças no ${surface} aparecem nas respostas dentro de uma hora.`,
      fewTimes: 'Algumas vezes por dia',
      fewTimesHint: (surface) => `Mais leve para o ${surface}. Serve para políticas e manuais.`,
      daily: 'Uma vez por dia',
      dailyHint: 'Para pastas que mudam raramente.'
    },
    disconnect: 'Desconectar…',
    disconnectTitle: (name) => `Desconectar ${name}?`,
    disconnectBody: (fmt, surface, docs) =>
      `Nada novo vai chegar do ${surface}. Escolha o que acontece com ${plural(fmt, docs, { one: `o ${fmt.formatCount(docs)} documento que já está aqui`, other: `os ${fmt.formatCount(docs)} documentos que já estão aqui` })}.`,
    keepThem: 'Manter',
    keepThemHint: (baseName, surface) =>
      `Eles ficam em ${baseName} e deixam de ser mantidos em dia. Os links deles de volta para o ${surface} continuam funcionando.`,
    withdrawThem: 'Retirar',
    withdrawThemHint:
      'Eles saem de todas as respostas futuras. As citações já dadas continuam funcionando e dizem que o documento foi retirado.',
    confirmDisconnect: 'Desconectar',
    keepSyncing: 'Continuar sincronizando',
    retry: 'Tentar estes de novo',
    retryHint: (surface) =>
      `O ${surface} recusou estes arquivos. Isso costuma ser temporário e eles são tentados de novo na próxima verificação.`,
    failedHeading: (fmt, count) =>
      `${fmt.formatCount(count)} ${plural(fmt, count, { one: 'arquivo não pôde ser buscado', other: 'arquivos não puderam ser buscados' })}`,
    twoCopies:
      'O mesmo arquivo em duas pastas conectadas é carregado duas vezes, uma para cada. Tirar de uma delas deixa a outra respondendo.',
    rebind: 'Usar outra conta',
    rebindHint: 'A pasta mantém tudo o que carregou; nada é buscado de novo.',
    currentAccount: 'a conta que esta pasta usa agora',
    close: 'Fechar'
  },

  // --- documentos sincronizados na tabela e na gaveta ------------------------
  document: {
    fromFolder: 'de uma pasta conectada',
    where: 'De onde veio',
    folder: 'pasta',
    account: 'conta',
    changed: 'alterado',
    hereSince: 'aqui desde',
    // Os quatro nomes de provedor são masculinos, então a contração é sempre "no"; a pasta
    // conectada é feminina e tem a entrada dela logo abaixo, em vez de entrar aqui como
    // parâmetro.
    openIn: (surface) => `Abrir no ${surface}`,
    opensNewTab: (surface) => `abre o ${surface} em uma nova aba`,
    openAtSource: 'Abrir na pasta conectada',
    opensNewTabAtSource: 'abre a pasta conectada em uma nova aba',
    fetchAgain: 'Buscar de novo',
    fetching: 'Buscando…',
    removeFooter: (path) =>
      `Remover tira os trechos dele de todas as respostas futuras e deixa o documento de fora de ${path} de agora em diante. Você pode colocá-lo de volta pela pasta.`,
    removeAndLeaveOut: 'Remover e deixar de fora',
    removeConfirm: (fmt, title, surface, path, passages) => {
      const consequence =
        passages === 0
          ? 'Nada do que ele trouxe está em alguma resposta futura'
          : pluralCategory(fmt.locale, passages) === 'one'
            ? `O ${fmt.formatCount(passages)} trecho dele sai de todas as respostas futuras`
            : `Os ${fmt.formatCount(passages)} trechos dele saem de todas as respostas futuras`
      return `Remover “${title}”? Ele veio do ${surface} · ${path}. ${consequence}, e ele fica de fora daquela pasta de agora em diante, então a próxima verificação não vai trazê-lo de volta. Você pode colocá-lo de volta pela pasta a qualquer momento. As citações já dadas continuam funcionando e dizem que ele foi retirado.`
    }
  },

  reason: (reason, detail) => {
    if (reason === null) return ''
    if (reason === 'unsupported' && detail) return `${detail} — não dá para ler`
    return EXCLUSION_LABEL[reason]
  }
}
